/**
 * VendorModern - MIGRATED to use CRUDPage template
 * 
 * Before: 396 lines
 * After: ~140 lines (65% reduction!)
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Typography, TextField, Button, Checkbox, FormControlLabel, CircularProgress,
  Divider, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { Building2, Phone, Mail, MapPin, X, Users, UserPlus, Trash2, Link2 } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

import CRUDPage from '../Components/UI/CRUDPage';
import { ActionMenu } from '../Components/UI/ResponsiveTable';
import { useOrg } from '../contexts/OrgContext';
import BASE_URL from '../config';

const getToken = async () => {
  const user = getAuth().currentUser;
  return user ? await user.getIdToken() : null;
};

// ─────────────────────────────────────────────────────────────────────────────
// People (vendor members) dialog
// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FORM = { member_name: '', designation: '', mobile: '', email: '', is_primary_contact: false, user_id: '' };

const PeopleDialog = ({ open, onClose, provider, organisationId }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [linkableUsers, setLinkableUsers] = useState([]);
  const [linkingMember, setLinkingMember] = useState(null);
  const [linkUserId, setLinkUserId] = useState('');
  const [linking, setLinking] = useState(false);

  const fetchMembers = async () => {
    if (!provider?.id || !organisationId) return;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/service-providers/${provider.id}/members`, {
        params: { organisation_id: organisationId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(res.data || []);
    } catch (err) {
      console.error('Error fetching vendor members:', err);
      toast.error('Failed to load people');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkableUsers = async () => {
    if (!provider?.id || !organisationId) return;
    try {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/service-providers/${provider.id}/members/linkable`, {
        params: { organisation_id: organisationId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLinkableUsers(res.data?.users || []);
    } catch (err) {
      console.error('Error fetching linkable users:', err);
    }
  };

  useEffect(() => {
    if (open) { fetchMembers(); fetchLinkableUsers(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider?.id]);

  const openAddForm = () => { setEditingMember(null); setForm(EMPTY_FORM); setFormOpen(true); };
  const openEditForm = (m) => {
    setEditingMember(m);
    setForm({
      member_name: m.member_name || '', designation: m.designation || '', mobile: m.mobile || '',
      email: m.email || '', is_primary_contact: !!m.is_primary_contact, user_id: '',
    });
    setFormOpen(true);
  };

  const handleSelectLinkedUser = (userId) => {
    const user = linkableUsers.find(u => u.id === userId);
    setForm(f => ({
      ...f,
      user_id: userId,
      member_name: f.member_name || user?.name || '',
      email: f.email || user?.email || '',
    }));
  };

  const handleSaveMember = async () => {
    if (!form.member_name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const token = await getToken();
      const payload = {
        organisation_id: organisationId,
        member_name: form.member_name.trim(),
        designation: form.designation.trim() || undefined,
        mobile: form.mobile.trim() || undefined,
        email: form.email.trim() || undefined,
        is_primary_contact: form.is_primary_contact,
      };
      if (form.user_id) payload.user_id = form.user_id;
      if (editingMember) {
        await axios.patch(`${BASE_URL}/api/service-provider-members/${editingMember.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Person updated');
      } else {
        await axios.post(`${BASE_URL}/api/service-providers/${provider.id}/members`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Person added');
      }
      setFormOpen(false);
      fetchMembers();
      fetchLinkableUsers();
    } catch (err) {
      console.error('Error saving vendor member:', err);
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Failed to save person');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove ${m.member_name} from this vendor?`)) return;
    try {
      const token = await getToken();
      await axios.delete(`${BASE_URL}/api/service-provider-members/${m.id}`, {
        params: { organisation_id: organisationId },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Person removed');
      fetchMembers();
      fetchLinkableUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove person');
    }
  };

  const handleLinkUser = async () => {
    if (!linkingMember || !linkUserId) return;
    setLinking(true);
    try {
      const token = await getToken();
      await axios.patch(`${BASE_URL}/api/service-provider-members/${linkingMember.id}`, {
        organisation_id: organisationId,
        user_id: linkUserId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('App user linked');
      setLinkingMember(null);
      setLinkUserId('');
      fetchMembers();
      fetchLinkableUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to link user');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Users size={20} />
          People — {provider?.service_provider_name}
        </Box>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : members.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No people added yet for this vendor.
          </Typography>
        ) : (
          members.map(m => (
            <Box
              key={m.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25,
                borderBottom: '1px solid', borderColor: 'divider',
                opacity: m.is_active === false ? 0.5 : 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>{m.member_name}</Typography>
                  {m.is_primary_contact && <Chip label="Primary" size="small" sx={{ bgcolor: '#6BA36822', color: '#4d7a4a', height: 20, fontSize: 11 }} />}
                  {m.has_login && <Chip label="App access" size="small" sx={{ bgcolor: '#6C8EBF22', color: '#47638F', height: 20, fontSize: 11 }} />}
                  {m.is_active === false && <Chip label="Inactive" size="small" sx={{ height: 20, fontSize: 11 }} />}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {[m.designation, m.mobile, m.email].filter(Boolean).join(' · ') || '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                {!m.has_login && m.is_active !== false && (
                  <Tooltip title={linkableUsers.length ? 'Link app user' : 'No linkable contractor users available'}>
                    <span>
                      <IconButton size="small" disabled={!linkableUsers.length} onClick={() => { setLinkingMember(m); setLinkUserId(''); }}>
                        <Link2 size={16} color="#E0A458" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {m.is_active !== false && (
                  <>
                    <Button size="small" onClick={() => openEditForm(m)}>Edit</Button>
                    <IconButton size="small" onClick={() => handleDelete(m)}>
                      <Trash2 size={16} color="#D2726A" />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" startIcon={<UserPlus size={16} />} onClick={openAddForm}>
          Add person
        </Button>
      </DialogActions>

      {/* Add / edit person form */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingMember ? 'Edit person' : 'Add person'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Name" required fullWidth size="small" value={form.member_name}
            onChange={e => setForm(f => ({ ...f, member_name: e.target.value }))} />
          <TextField label="Designation" fullWidth size="small" value={form.designation}
            onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} />
          <TextField label="Mobile" fullWidth size="small" value={form.mobile}
            onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} />
          <TextField label="Email" fullWidth size="small" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <FormControlLabel
            control={<Checkbox checked={form.is_primary_contact} onChange={e => setForm(f => ({ ...f, is_primary_contact: e.target.checked }))} size="small" />}
            label="Primary contact"
          />
          {!editingMember && (
            <>
              <Divider />
              <FormControl fullWidth size="small">
                <InputLabel>App user (Contractor role)</InputLabel>
                <Select
                  label="App user (Contractor role)"
                  value={form.user_id}
                  onChange={e => handleSelectLinkedUser(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {linkableUsers.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Only users holding the Contractor role can be linked. Create the account on the Users page first.
                </Typography>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveMember} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link an existing app user (contact-only member) dialog */}
      <Dialog open={!!linkingMember} onClose={() => setLinkingMember(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Link app user to {linkingMember?.member_name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>App user (Contractor role)</InputLabel>
            <Select
              label="App user (Contractor role)"
              value={linkUserId}
              onChange={e => setLinkUserId(e.target.value)}
            >
              {linkableUsers.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Only users holding the Contractor role can be linked. Create the account on the Users page first.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLinkingMember(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleLinkUser} disabled={linking || !linkUserId}>
            {linking ? <CircularProgress size={16} /> : 'Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

function VendorModern() {
  const { selectedOrgId } = useOrg();
  const [serviceTypes, setServiceTypes] = useState([]);

  const [peopleVendor, setPeopleVendor] = useState(null);
  const [peopleDialogOpen, setPeopleDialogOpen] = useState(false);

  const handleManagePeople = (row) => {
    setPeopleVendor({ id: row.vendor_uuid, service_provider_name: row.service_provider_name });
    setPeopleDialogOpen(true);
  };

  // Fetch service types for dropdown
  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const user = getAuth().currentUser;
        const token = await user.getIdToken();

        const response = await axios.get(`${BASE_URL}/api/asset-types`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setServiceTypes(response.data || []);
      } catch (error) {
        console.error('Error fetching service types:', error);
      }
    };

    fetchServiceTypes();
  }, []);

  // Field definitions - dynamically update options when serviceTypes change
  // Note: service_provider_code is auto-generated by database trigger, not shown in form
  const vendorFields = [
    {
      name: 'service_provider_name',
      label: 'Vendor Name',
      type: 'text',
      required: true,
      validation: {
        required: 'Vendor name is required',
        minLength: { value: 2, message: 'Name must be at least 2 characters' }
      }
    },
    {
      name: 'service_type',
      label: 'Service Type',
      type: 'select',
      required: true,
      validation: {
        required: 'Service type is required'
      },
      options: serviceTypes.map(st => ({
        value: st.id.toString(),
        label: st.name
      }))
    },
    {
      name: 'mobile',
      label: 'Mobile Number',
      type: 'text',
      required: true,
      validation: {
        required: 'Mobile number is required',
        pattern: {
          value: /^[0-9+\-\s()]+$/,
          message: 'Invalid phone number format'
        }
      }
    },
    {
      name: 'email_id',
      label: 'Email',
      type: 'email',
      required: true,
      validation: {
        required: 'Email is required',
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Invalid email address'
        }
      }
    },
    {
      name: 'address',
      label: 'Address',
      type: 'text',
      multiline: true,
      rows: 2
    },
    {
      name: 'city',
      label: 'City',
      type: 'text'
    }
  ];

  // Column definitions
  const columns = [
    {
      field: 'service_provider_code',
      headerName: 'Vendor Code',
      sortable: true,
      renderCell: ({ value }) => (
        <Chip
          label={value || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      )
    },
    {
      field: 'service_provider_name',
      headerName: 'Vendor Name',
      sortable: true,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Building2 size={16} color="#6C8EBF" />
          <span style={{ fontWeight: 500 }}>{value}</span>
        </Box>
      )
    },
    {
      field: 'service_type_name',
      headerName: 'Service Type',
      sortable: true,
      renderCell: ({ value }) => (
        <Chip
          label={value || 'N/A'}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      field: 'mobile',
      headerName: 'Mobile',
      sortable: false,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <Phone size={14} />
          <span>{value || '-'}</span>
        </Box>
      )
    },
    {
      field: 'email_id',
      headerName: 'Email',
      sortable: false,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <Mail size={14} />
          <span>{value || '-'}</span>
        </Box>
      )
    },
    {
      field: 'city',
      headerName: 'City',
      sortable: true,
      renderCell: ({ value }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <MapPin size={14} />
          <span>{value || 'N/A'}</span>
        </Box>
      )
    },
    {
      field: 'people',
      headerName: 'People',
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => handleManagePeople(row)}
        >
          <Chip
            icon={<Users size={14} />}
            label={row.member_count ?? 0}
            size="small"
            variant="outlined"
          />
          {row.primary_contact_name && (
            <Typography variant="caption" color="text.secondary">{row.primary_contact_name}</Typography>
          )}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      align: 'right'
    }
  ];

  // Custom data transformation for edit (convert service_type to string for form)
  const transformForEdit = (data) => ({
    ...data,
    service_type: data.service_type?.toString() || ''
  });

  // Don't send service_provider_code to backend (will be auto-generated by database trigger)
  const transformForCreate = (data) => {
    const { service_provider_code, ...rest } = data;
    return rest;
  };

  return (
    <>
      <CRUDPage
        title="Vendor Management"
        subtitle="Manage service providers and vendors"
        icon={Building2}
        columns={columns}
        fields={vendorFields}
        searchFields={['service_provider_name', 'service_provider_code', 'service_type_name', 'mobile', 'email_id', 'city']}
        permissions={{
          resource: 'Vendor',
          module: 'vendorCreation'
        }}
        renderCustomActions={(params) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Tooltip title="People">
              <IconButton size="small" onClick={() => handleManagePeople(params.row)}>
                <Users size={16} />
              </IconButton>
            </Tooltip>
            <ActionMenu
              rowData={params.row}
              onView={params.onView}
              onEdit={params.onEdit}
              onDelete={params.onDelete}
            />
          </Box>
        )}
        crudConfig={{
          endpoint: '/api/service-providers',
          entityName: 'Vendor',
          entityNamePlural: 'Vendors',
          useOrgId: true,
          idField: 'service_provider_code', // Vendors use service_provider_code as primary key
          transformCreate: transformForCreate,
          transformFetch: (fetchedData) => {
            // Map service_provider_code to id for table display, keep original for backend,
            // and preserve the real vendor UUID separately for the People dialog.
            return fetchedData.map(vendor => ({
              ...vendor,
              id: vendor.service_provider_code, // Map to id for table
              service_provider_code: vendor.service_provider_code, // Keep for backend
              vendor_uuid: vendor.id, // Real UUID, used by handleManagePeople
            }));
          },
          transformForEdit,
          deleteWarning: 'Agreements associated with this vendor may be affected.'
        }}
      />
      <PeopleDialog
        open={peopleDialogOpen}
        onClose={() => setPeopleDialogOpen(false)}
        provider={peopleVendor}
        organisationId={selectedOrgId}
      />
    </>
  );
}

export default VendorModern;
